import React, { memo, useEffect, useRef } from 'react';
import { CSSObject, TextField, InputAdornment } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface InputProps {
    name: string;
    placeholder?: string;
    onChange: (name: string, value: string) => void;
    sx?: CSSObject;
    testId?: string;
    value?: string;
}

const Input: React.FC<InputProps> = ({ placeholder, value, name, onChange, testId, sx = {} }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(name, e.target.value);
    };

    useEffect(() => {
        if (inputRef.current && value === '') {
            inputRef.current.value = '';
        }
    }, [onChange, name, value]);

    const handleInputClear = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
            onChange(name, '');
        }
    };

    const searchAdornmentIcon = (
        <SearchOutlinedIcon
            data-testid="SearchOutlinedIcon"
            sx={{
                width: 24,
                color: 'common.grey1',
            }}
        />
    );

    const closeAdornmentIcon = (
        <CloseIcon
            data-testid="CloseIcon"
            onClick={handleInputClear}
            sx={{
                cursor: 'pointer',
                width: 24,
                color: 'common.grey1',
            }}
        />
    );

    return (
        <TextField
            placeholder={placeholder}
            data-testid={testId ? testId : 'form-input'}
            variant='outlined'
            fullWidth
            inputRef={inputRef}
            onChange={onValueChange}
            value={value}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {value?.length ? closeAdornmentIcon : searchAdornmentIcon}
                    </InputAdornment>
                ),
            }}
            sx={{
                '& .MuiInputBase-root.MuiOutlinedInput-root ::placeholder': {
                    fontSize: 14,
                    opacity: 1,
                    color: 'common.grey1',
                },
                '& .MuiOutlinedInput-root': {
                    height: '48px',
                    '& fieldset': {
                        borderColor: 'common.grey2',
                        borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                        borderColor: 'common.grey2',
                    },
                    '&.Mui-focused fieldset': {
                        borderWidth: '1px',
                        borderColor: 'common.primary',
                    },
                },
                '& .MuiFormHelperText-root.Mui-error': {
                    color: 'common.system.error.main',
                },
                ...sx,
            }}
        />
    );
};

export default memo(Input);